import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Film } from "lucide-react";

/**
 * FeatureFilmTeaserBand — landing-page slot for Halflight (in-house
 * animated feature). Founder plain-ink 2026-02-15: the picture-lock is
 * still cooking; the teaser stays on the landing as a "in the forge"
 * state so the shape is visible while the assembly is in progress.
 */
const HALFLIGHT_POSTER = "/art/film_poster.jpg";

export default function FeatureFilmTeaserBand() {
  return (
    <section
      id="feature-teasers"
      data-testid="feature-teaser-band"
      className="relative w-full bg-[#050505] border-t border-white/[0.05] py-20 sm:py-28 px-6 sm:px-10"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-[10px] uppercase tracking-[0.45em] text-[#ff5a00] text-center"
        >
          The Proof · In-House Animated Feature
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="font-display uppercase text-center leading-[0.95] text-[#ededed] mt-6"
          style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
        >
          One feature film.
          <br />
          <span className="italic text-[#ff5a00]">Cut inside the forge.</span>
        </motion.h2>

        <div className="mt-16 max-w-[1100px] mx-auto">
          {/* HALFLIGHT — feature film (single-card, no link while in the forge) */}
          <motion.div
            data-testid="teaser-halflight"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden border border-white/[0.08]"
          >
            <div className="relative aspect-[21/9]">
              <div
                className="absolute inset-0 bg-center bg-cover"
                style={{ backgroundImage: `url(${HALFLIGHT_POSTER})` }}
                role="img"
                aria-label="Halflight — feature film poster"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/95" />
              <div className="absolute top-5 left-5 font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] border border-[#ff5a00]/60 bg-black/60 backdrop-blur-sm px-3 py-1">
                Feature Film · 101 min
              </div>
              <div className="absolute top-5 right-5 font-mono text-[9px] uppercase tracking-[0.32em] text-[#a1a1aa] border border-white/15 bg-black/60 backdrop-blur-sm px-3 py-1">
                In-House Animated
              </div>
            </div>
            <div className="absolute inset-0 flex flex-col items-start justify-end px-8 sm:px-12 pb-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-3">
                Halflight
              </p>
              <h3
                className="font-display uppercase leading-[0.95] text-[#f5f0e8] drop-shadow-[0_2px_24px_rgba(0,0,0,0.9)]"
                style={{ fontSize: "clamp(2.25rem, 5.4vw, 4rem)" }}
              >
                A Year of First Light
              </h3>
              <p className="font-display italic text-lg sm:text-xl md:text-2xl text-[#d4d4d8] mt-4 max-w-2xl leading-snug">
                Three girls, one microphone, one year to make the room stop looking at them like it already knew how the year would end.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
                <Film className="w-3.5 h-3.5" />
                In the forge · sound coming first
              </span>
            </div>
          </motion.div>
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/what-we-collapse"
            data-testid="teaser-band-footer-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.42em] text-[#a1a1aa] hover:text-[#ff5a00] transition-colors border border-[#27272a] hover:border-[#ff5a00]/60 px-6 py-3"
          >
            See what else we collapse ↗
          </Link>
        </div>
      </div>
    </section>
  );
}
